"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import type {
  CandidateEditableProfileDto,
  CandidateSessionResponseDto,
  JobItemDto,
} from "@artha/api-contracts";

import {
  clearCandidateIdentity,
  readCandidateIdentity,
  writeCandidateIdentity,
  type CandidateIdentityStorage,
} from "../services/storage";
import {
  fetchCandidateSession,
  logoutCandidate,
} from "../services/personalizationClient";

import { isResumePersonalizationUiEnabled } from "../personalization.constants";
import type { CandidateProfileDraft } from "../personalization.types";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { fetchPublicJobs } from "@/modules/common/services/jobApi";

import type { VerificationMode } from "../personalization.types";

export const hasPersonalizedProfile = (
  profile: CandidateEditableProfileDto | null | undefined,
) =>
  Boolean(
    profile &&
    (profile.titles.length > 0 ||
      profile.skills.length > 0 ||
      profile.keywords.length > 0),
  );

export const useCandidatePersonalizationState = (creatorId?: string) => {
  const t = useTranslations("profile.resumePersonalization");
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  useEffect(() => {
    posthogRef.current = posthog;
  }, [posthog]);
  const [identity, setIdentity] = useState<CandidateIdentityStorage>(
    readCandidateIdentity,
  );
  const [editableProfile, setEditableProfile] =
    useState<CandidateEditableProfileDto | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [isSessionLoading, setIsSessionLoading] = useState(
    isResumePersonalizationUiEnabled,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [verificationMode, setVerificationMode] =
    useState<VerificationMode>("profile");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [jobsRefreshVersion, setJobsRefreshVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const capturePersonalizationEvent = useCallback(
    (
      event: string,
      properties: Record<string, string | boolean | undefined> = {},
    ) => {
      posthogRef.current?.capture?.(event, {
        ...(creatorId && { portal_id: creatorId }),
        ...properties,
      });
    },
    [creatorId],
  );

  const applySession = useCallback(
    (session: CandidateSessionResponseDto, refreshJobs: boolean) => {
      if (!session.authenticated || !session.externalId) return;
      const isPersonalized =
        session.hasResume || hasPersonalizedProfile(session.editableProfile);
      const nextIdentity: CandidateIdentityStorage = {
        externalId: session.externalId,
        resumePersonalized: isPersonalized,
        candidatePreferences: session.preferences ?? null,
      };
      setIdentity(nextIdentity);
      writeCandidateIdentity(nextIdentity);
      setEditableProfile(session.editableProfile ?? null);
      setHasResume(session.hasResume);
      setIsAuthenticated(true);
      setError(null);
      if (refreshJobs && isPersonalized) {
        setJobsRefreshVersion((version) => version + 1);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isResumePersonalizationUiEnabled) return;

    let active = true;
    void fetchCandidateSession()
      .then((session) => {
        if (!active) return;
        if (session.authenticated) {
          applySession(session, true);
        } else {
          setIdentity({
            externalId: null,
            resumePersonalized: false,
            candidatePreferences: null,
          });
          clearCandidateIdentity();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsSessionLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applySession]);

  const getPosthogDistinctId = useCallback(() => {
    const distinctId = posthog?.get_distinct_id?.();
    if (!distinctId) {
      throw new Error(t("errors.identityNotReady"));
    }
    return distinctId;
  }, [posthog, t]);

  const refreshSession = useCallback(async () => {
    const session = await fetchCandidateSession();
    applySession(session, false);
  }, [applySession]);

  const restoreProfileDraft = useCallback((draft: CandidateProfileDraft) => {
    setIdentity((current) => ({ ...current, externalId: draft.externalId }));
    setEditableProfile(draft.editableProfile);
    setHasResume(true);
  }, []);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    setError(null);
    try {
      await logoutCandidate();
      capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGOUT, {
        ...(editableProfile?.email && {
          email: editableProfile.email.trim().toLowerCase(),
        }),
      });
      clearCandidateIdentity();
      setIdentity({
        externalId: null,
        resumePersonalized: false,
        candidatePreferences: null,
      });
      setEditableProfile(null);
      setHasResume(false);
      setIsAuthenticated(false);
      setMaskedEmail("");
      posthog?.reset?.();
      setJobsRefreshVersion((version) => version + 1);
    } catch (logoutError) {
      const message =
        logoutError instanceof Error
          ? logoutError.message
          : t("errors.logoutFailed");
      setError(message);
      throw logoutError;
    } finally {
      setIsLoggingOut(false);
    }
  }, [capturePersonalizationEvent, editableProfile, posthog, t]);

  const getAlternativeJobs = useCallback(
    async (
      profile: CandidateEditableProfileDto,
      currentJob: Pick<JobItemDto, "id" | "ref_id" | "country">,
    ) => {
      const result = await fetchPublicJobs(
        "",
        {
          limit: 6,
          offset: 0,
          location: profile.location?.country || currentJob.country || "US",
        },
        {
          baseUrl: "/api/job-api",
          candidatePreferences: {
            titles: profile.titles,
            skills: profile.skills,
            keywords: profile.keywords,
          },
          creator: creatorId ? { id: creatorId } : undefined,
          visitorId: getPosthogDistinctId(),
          pulseVariant: "control",
        },
      );
      return result.items
        .filter(
          (job) =>
            job.id !== currentJob.id &&
            (!currentJob.ref_id || job.ref_id !== currentJob.ref_id),
        )
        .slice(0, 5);
    },
    [creatorId, getPosthogDistinctId],
  );

  return {
    t,
    posthog,
    identity,
    setIdentity,
    editableProfile,
    setEditableProfile,
    isAuthenticated,
    hasResume,
    setHasResume,
    isSessionLoading,
    isUploading,
    setIsUploading,
    isRequestingCode,
    setIsRequestingCode,
    isVerifying,
    setIsVerifying,
    isLoggingOut,
    verificationMode,
    setVerificationMode,
    maskedEmail,
    setMaskedEmail,
    jobsRefreshVersion,
    error,
    setError,
    capturePersonalizationEvent,
    applySession,
    getPosthogDistinctId,
    refreshSession,
    restoreProfileDraft,
    logout,
    getAlternativeJobs,
  };
};
